import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
    Alert,
    AppBar,
    Box,
    Container,
    Dialog,
    IconButton,
    Toolbar,
    Typography,
} from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import AutoFillGrid from "@/components/AutoFillGrid";
import IconButtonLink from "@/components/Link/IconButtonLink";
import { useQueryOptionsFactory } from "@/queries";
import RegistrationCard from "./-components/RegistrationCard";

const Root = (): ReactNode => {
    const [open, setOpen] = useState(true);
    const navigate = Route.useNavigate();
    const qof = useQueryOptionsFactory();
    const { auctionId } = Route.useParams();
    const auction = useSuspenseQuery(qof.auction.get(auctionId)).data;
    const registrations = useSuspenseQuery(qof.registration.list(auctionId)).data;

    return (
        <Dialog
            open={open}
            onClose={() => {
                setOpen(false);
            }}
            fullScreen
            slotProps={{
                transition: {
                    onExited: () => {
                        void navigate({
                            to: "/manage/auctions",
                            search: true,
                        });
                    },
                },
            }}
        >
            <AppBar position="static">
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={() => {
                            setOpen(false);
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" component="div">
                        {auction.title}
                    </Typography>
                    <IconButtonLink
                        to="/manage/auctions/$auctionId/registrations/create"
                        params={{ auctionId }}
                        edge="end"
                        color="inherit"
                        sx={{ ml: "auto" }}
                        disabled={auction.state === "completed"}
                    >
                        <AddIcon />
                    </IconButtonLink>
                </Toolbar>
            </AppBar>
            <Box component="main" sx={{ py: 2 }}>
                <Container>
                    {registrations.length === 0 && (
                        <Alert severity="info">Nobody has registered for the auction yet.</Alert>
                    )}

                    {registrations.length > 0 && (
                        <AutoFillGrid minWidth={300}>
                            {registrations.map((registration) => (
                                <RegistrationCard
                                    key={registration.id}
                                    auction={auction}
                                    registration={registration}
                                />
                            ))}
                        </AutoFillGrid>
                    )}
                </Container>

                <Outlet />
            </Box>
        </Dialog>
    );
};

export const Route = createFileRoute("/manage/auctions/$auctionId/registrations")({
    component: Root,
    loader: async ({ context, params }) => {
        await context.queryClient.ensureQueryData(context.qof.registration.list(params.auctionId));
    },
});
